import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import {
  useToast,
  Box,
  Select,
  Input,
  Button,
  Skeleton,
  Table,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Heading,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import { IDevice } from "../types/deviceInterface";
import { translateCropTypeName } from "../utils/translateCropTypeName";

const crops = [
  { key: "grass", label: "Grass" },
  { key: "apple", label: "Apple" },
  { key: "cherry", label: "Cherry" },
  { key: "wine_grape", label: "Wine Grape" },
  { key: "olive", label: "Olive" },
];

type GDDResult = {
  accumulated_gdd: number;
  current_stage: string;
  crop_type: string;
};
type SeriesPoint = {
  timestamp: string;
  daily_gdd: number;
  cumulative_gdd: number;
};

const GDDView: React.FC = () => {
  const api = useAxios();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.900");

  const [devices, setDevices] = useState<IDevice[]>([]);
  const [deviceId, setDeviceId] = useState<number>();
  const [cropKey, setCropKey] = useState<string>(crops[0].key);
  const [startDate, setStartDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<GDDResult | null>(null);
  const [series, setSeries] = useState<SeriesPoint[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const dRes = await api.get("frontend/user-devices/");
        setDevices(dRes.data);
        if (dRes.data[0]) setDeviceId(dRes.data[0].id);
      } catch {
        toast({
          title: "Error loading devices",
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const run = async () => {
    if (!deviceId) return;
    setCalculating(true);
    try {
      const resp = await api.get("agtech/degree-days-calculator", {
        params: {
          crop: cropKey,
          user_device_id: deviceId,
          start_date: startDate,
        },
      });
      const { series, accumulated_gdd, current_stage, crop_type } = resp.data;
      setSeries(series);
      setResult({ accumulated_gdd, current_stage, crop_type });
    } catch {
      toast({
        title: "Error calculating GDD",
        status: "error",
        isClosable: true,
      });
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Navbar>
      <Box maxW="600px" mx="auto" p={4} bg={bg} borderRadius="md">
        <Heading mb={4}>Growing Degree Days</Heading>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
          {loading ? (
            <Skeleton height="40px" />
          ) : (
            <Select value={deviceId} onChange={(e) => setDeviceId(+e.target.value)}>
              {devices.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {d.device_name}
                </option>
              ))}
            </Select>
          )}
          <Select value={cropKey} onChange={(e) => setCropKey(e.target.value)}>
            {crops.map((c) => (
              <option key={c.key} value={c.key}>
                {translateCropTypeName(c.label)}
              </option>
            ))}
          </Select>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Button onClick={run} colorScheme="teal" isLoading={calculating}>
            Run
          </Button>
        </SimpleGrid>
        {result && (
          <>
            <Box mb={4}>
              <Table variant="simple">
                <Tbody>
                  <Tr>
                    <Th>Crop Type</Th>
                    <Td>{translateCropTypeName(result.crop_type)}</Td>
                  </Tr>
                  <Tr>
                    <Th>Accumulated GDD</Th>
                    <Td>{result.accumulated_gdd.toFixed(2)}</Td>
                  </Tr>
                  <Tr>
                    <Th>Current Stage</Th>
                    <Td>{result.current_stage}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            <Box height={400} mb={4}>
              <Line
                data={{
                  labels: series.map((p) => new Date(p.timestamp).toLocaleDateString()),
                  datasets: [
                    {
                      label: "Daily GDD",
                      data: series.map((p) => p.daily_gdd),
                      borderColor: "blue",
                      fill: false,
                    },
                    {
                      label: "Cumulative GDD",
                      data: series.map((p) => p.cumulative_gdd),
                      borderColor: "green",
                      fill: false,
                    },
                  ],
                }}
                options={{
                  scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }],
                    xAxes: [{}],
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Navbar>
  );
};

export default GDDView;
